<div class="fundraising-comparison">
  <div class="resource-surface rounded-4 bg-white border p-3 p-md-4">
    <div *ngIf="errorMessage" class="alert alert-danger mb-0" role="alert">
      {{ errorMessage }}
    </div>

    <ng-container *ngIf="leftResult && rightResult && scenario">
      <app-fundraiser-scenario
        [scenario]="scenario"
        [donationCountOverride]="state.donationCountOverride"
        (intendedGiftTotalCentsChange)="onIntendedGiftTotalCentsChange($event)"
        (donationCountOverrideChange)="onDonationCountOverrideChange($event)"
      ></app-fundraiser-scenario>

      <hr class="my-4" />

      <section aria-labelledby="versus-results-heading">
        <div class="text-center mb-4">
          <p class="text-uppercase small fw-semibold text-primary mb-2">
            Side-by-side fundraiser result
          </p>
          <h2 id="versus-results-heading" class="h3 fw-bold mb-0">
            Compare the same online donations
          </h2>
        </div>

        <div class="row g-2 g-md-3 mb-3">
          <div class="col-6">
            <div class="border rounded-3 h-100 p-2 p-md-3">
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <h3 class="h5 fw-bold mb-0">{{ leftResult.platformName }}</h3>
                <span
                  class="badge border"
                  [ngClass]="evidenceBadgeClasses(leftResult.evidence.status)"
                >
                  {{ leftResult.evidence.label }}
                </span>
              </div>
              <p *ngIf="configurationSummary(leftResult)" class="small mb-0">
                {{ configurationSummary(leftResult) }}
              </p>
            </div>
          </div>
          <div class="col-6">
            <div class="border rounded-3 h-100 p-2 p-md-3">
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <h3 class="h5 fw-bold mb-0">{{ rightResult.platformName }}</h3>
                <span
                  class="badge border"
                  [ngClass]="evidenceBadgeClasses(rightResult.evidence.status)"
                >
                  {{ rightResult.evidence.label }}
                </span>
              </div>
              <p *ngIf="configurationSummary(rightResult)" class="small mb-0">
                {{ configurationSummary(rightResult) }}
              </p>
            </div>
          </div>
        </div>

        <div class="border rounded-3 overflow-hidden">
          <div class="row g-0 border-bottom py-3 px-2 px-md-3">
            <p class="col-12 small fw-semibold text-body-secondary mb-2">
              Online donations
            </p>
            <p
              class="col-6 h5 fw-bold mb-0"
              [attr.aria-label]="
                leftResult.platformName +
                ' online donations: ' +
                formatMoney(leftResult.intendedGiftTotalCents)
              "
            >
              {{ formatMoney(leftResult.intendedGiftTotalCents) }}
            </p>
            <p
              class="col-6 h5 fw-bold mb-0"
              [attr.aria-label]="
                rightResult.platformName +
                ' online donations: ' +
                formatMoney(rightResult.intendedGiftTotalCents)
              "
            >
              {{ formatMoney(rightResult.intendedGiftTotalCents) }}
            </p>
          </div>
          <div class="row g-0 border-bottom bg-light py-3 px-2 px-md-3">
            <p class="col-12 small fw-semibold text-body-secondary mb-2">
              Organization receives
            </p>
            <div class="col-6 pe-2">
              <p
                class="h4 fw-bold text-primary mb-0"
                [attr.aria-label]="
                  leftResult.platformName +
                  ' organization receives: ' +
                  formatMoney(organizationReceivesCents(leftResult))
                "
              >
                {{ formatMoney(organizationReceivesCents(leftResult)) }}
              </p>
              <p
                *ngIf="costsExceedDonationsCents(leftResult) > 0"
                class="small text-danger mb-0 mt-1"
              >
                Costs exceed donations by
                {{ formatMoney(costsExceedDonationsCents(leftResult)) }}
              </p>
            </div>
            <div class="col-6">
              <p
                class="h4 fw-bold text-primary mb-0"
                [attr.aria-label]="
                  rightResult.platformName +
                  ' organization receives: ' +
                  formatMoney(organizationReceivesCents(rightResult))
                "
              >
                {{ formatMoney(organizationReceivesCents(rightResult)) }}
              </p>
              <p
                *ngIf="costsExceedDonationsCents(rightResult) > 0"
                class="small text-danger mb-0 mt-1"
              >
                Costs exceed donations by
                {{ formatMoney(costsExceedDonationsCents(rightResult)) }}
              </p>
            </div>
          </div>
          <div class="row g-0 border-bottom py-3 px-2 px-md-3">
            <p class="col-12 small fw-semibold text-body-secondary mb-2">
              Organization pays
            </p>
            <p
              class="col-6 h5 fw-bold mb-0"
              [attr.aria-label]="
                leftResult.platformName +
                ' organization pays: ' +
                formatMoney(leftResult.organizationCostCents)
              "
            >
              {{ formatMoney(leftResult.organizationCostCents) }}
            </p>
            <p
              class="col-6 h5 fw-bold mb-0"
              [attr.aria-label]="
                rightResult.platformName +
                ' organization pays: ' +
                formatMoney(rightResult.organizationCostCents)
              "
            >
              {{ formatMoney(rightResult.organizationCostCents) }}
            </p>
          </div>
          <div class="row g-0 py-3 px-2 px-md-3">
            <p class="col-12 small fw-semibold text-body-secondary mb-2">
              Supporters pay extra
            </p>
            <p
              class="col-6 fw-semibold mb-0 pe-2"
              [attr.aria-label]="
                leftResult.platformName +
                ' supporters pay extra: ' +
                supporterPaidLabel(leftResult)
              "
            >
              {{ supporterPaidLabel(leftResult) }}
            </p>
            <p
              class="col-6 fw-semibold mb-0"
              [attr.aria-label]="
                rightResult.platformName +
                ' supporters pay extra: ' +
                supporterPaidLabel(rightResult)
              "
            >
              {{ supporterPaidLabel(rightResult) }}
            </p>
          </div>
        </div>

        <div
          class="alert alert-secondary mt-3 mb-0"
          aria-label="Comparison summary"
        >
          <p class="fw-semibold mb-1">{{ receiptDifferenceLabel }}</p>
          <p class="small mb-0">{{ supporterDifferenceLabel }}</p>
        </div>

        <div class="row g-3 mt-1">
          <div class="col-12 col-lg-6">
            <div class="border rounded-3 h-100 p-3">
              <p
                class="small text-uppercase fw-semibold text-body-secondary mb-1"
              >
                {{ leftResult.platformName }} fee structure
              </p>
              <p class="fw-bold mb-0">{{ leftResult.compactFeeFormula }}</p>
              <app-platform-options
                *ngIf="leftResult.configurationUi"
                [platformId]="leftResult.platformId"
                [optionGroups]="leftResult.optionGroups"
                [selections]="leftResult.optionSelections"
                [configurationUi]="leftResult.configurationUi"
                [showSelectedSummary]="false"
                presentation="disclosure"
                (optionChange)="onOptionChange($event)"
              ></app-platform-options>
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div class="border rounded-3 h-100 p-3">
              <p
                class="small text-uppercase fw-semibold text-body-secondary mb-1"
              >
                {{ rightResult.platformName }} fee structure
              </p>
              <p class="fw-bold mb-0">{{ rightResult.compactFeeFormula }}</p>
              <app-platform-options
                *ngIf="rightResult.configurationUi"
                [platformId]="rightResult.platformId"
                [optionGroups]="rightResult.optionGroups"
                [selections]="rightResult.optionSelections"
                [configurationUi]="rightResult.configurationUi"
                [showSelectedSummary]="false"
                presentation="disclosure"
                (optionChange)="onOptionChange($event)"
              ></app-platform-options>
            </div>
          </div>
        </div>

        <details class="mt-4" (toggle)="onDetailsToggle($event)">
          <summary class="btn btn-link px-0 fw-semibold">
            View detailed calculations
          </summary>
          <div class="row g-3 mt-1">
            <div class="col-12 col-lg-6">
              <h3 class="h5 fw-bold mb-0">{{ leftResult.platformName }}</h3>
              <app-calculation-details
                [result]="leftResult"
                [scenario]="scenario"
                presentation="expanded"
                (pricingSourceOpened)="onPricingSourceOpened($event)"
              ></app-calculation-details>
            </div>
            <div class="col-12 col-lg-6">
              <h3 class="h5 fw-bold mb-0">{{ rightResult.platformName }}</h3>
              <app-calculation-details
                [result]="rightResult"
                [scenario]="scenario"
                presentation="expanded"
                (pricingSourceOpened)="onPricingSourceOpened($event)"
              ></app-calculation-details>
            </div>
          </div>
        </details>
      </section>

      <div
        class="d-flex flex-column flex-sm-row flex-wrap gap-2 mt-4 pt-4 border-top"
      >
        <a
          *ngIf="detailPageUrl(leftResult.platformId) as leftUrl"
          class="btn btn-outline-primary"
          [href]="leftUrl"
          (click)="onPlatformLinkClicked(leftResult.platformId)"
        >
          View {{ leftResult.platformName }} pricing
        </a>
        <a
          *ngIf="detailPageUrl(rightResult.platformId) as rightUrl"
          class="btn btn-outline-primary"
          [href]="rightUrl"
          (click)="onPlatformLinkClicked(rightResult.platformId)"
        >
          View {{ rightResult.platformName }} pricing
        </a>
        <a
          class="btn btn-primary"
          [href]="overviewUrl"
          (click)="onOverviewLinkClicked()"
        >
          Compare all fundraising platforms
        </a>
        <button
          type="button"
          class="btn btn-outline-primary"
          (click)="copyVersusLink()"
        >
          Copy comparison link
        </button>
        <p
          class="small text-body-secondary mb-0 align-self-center"
          role="status"
        >
          {{ copyStatus }}
        </p>
      </div>

      <p class="visually-hidden" aria-live="polite" aria-atomic="true">
        {{ liveAnnouncement }}
      </p>
    </ng-container>
  </div>
</div>
